import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ScrollView, StyleSheet } from 'react-native';

import { RouteProp, useRoute } from '@react-navigation/native';

import { LineSeparator } from '@/components/LineSeparator';
import { Screen } from '@/components/ScreenComponent';
import { ScreenNavBar } from '@/components/ScreenNavBar';
import { SizedBox } from '@/components/SizedBox';
import { Switch } from '@/components/Switch';
import { Text } from '@/components/TextComponent';
import { Row } from '@/components/lib/components/Row';
import { ds } from '@/components/lib/design.system';
import { useProductTransitionStore } from '@/feature/product-transition/use-product-transition-store';
import { MaxWidthWrapper } from '@/feature/responsive-design/WebComponents';
import {
    DefaultVertical,
    mapDefaultVerticalToProduct,
    mapProductToDefaultVertical,
    useAuthUserSettings,
    useUpdateUserSettings,
} from '@/hooks/use-auth-user-settings';
import { filterAvailableProducts } from '@/hooks/use-available-products';
import { Product, useJurisdictionStore } from '@/hooks/use-jurisdiction';
import { useUserStore } from '@/hooks/use-user-store';
import { AccountStackParamList } from '@/navigation/types';

export const HideVerticalsScreen = () => {
    const { t } = useTranslation(['product_switch_menu', 'rg']);
    const route = useRoute<RouteProp<AccountStackParamList, 'HideVerticals'>>();
    const navigateHomeOnBack = route.params?.navigateHomeOnBack;
    const [enabledProduct, setEnabledProduct] = useState<Product | null>(null);
    const profile = useUserStore(state => state.profile);
    const products = useJurisdictionStore(state => state.jurisdictionSettings?.products ?? []);
    const currentProduct = useJurisdictionStore(state => state.product);
    const { data: userSettings } = useAuthUserSettings();
    const { mutate: updateUserSettings, isPending } = useUpdateUserSettings();

    const availableVerticals = useMemo(() => {
        return filterAvailableProducts(products, profile?.email)
            .map(mapProductToDefaultVertical)
            .filter((vertical): vertical is DefaultVertical => vertical !== null);
    }, [products, profile?.email]);

    const hiddenVerticals = useMemo(() => userSettings?.hidden_verticals ?? [], [userSettings?.hidden_verticals]);

    const handleToggle = useCallback(
        (vertical: DefaultVertical) => {
            const isCurrentProduct = mapDefaultVerticalToProduct(vertical) === currentProduct;
            const isCurrentlyHidden = hiddenVerticals.includes(vertical);

            if (isCurrentProduct && !isCurrentlyHidden) {
                return;
            }

            const nextHiddenVerticals = hiddenVerticals.includes(vertical)
                ? hiddenVerticals.filter(item => item !== vertical)
                : [...hiddenVerticals, vertical];

            updateUserSettings(
                { hidden_verticals: nextHiddenVerticals },
                {
                    onSuccess: () => {
                        // Track the unhidden product for navigateHomeOnBack when all products are hidden.
                        if (isCurrentlyHidden && navigateHomeOnBack) {
                            const product = mapDefaultVerticalToProduct(vertical);
                            if (product !== Product.None) {
                                setEnabledProduct(product);
                            }
                        }

                        const currentVertical = mapProductToDefaultVertical(currentProduct);

                        if (currentVertical !== vertical || !nextHiddenVerticals.includes(vertical)) {
                            return;
                        }

                        const nextVisibleProduct = availableVerticals
                            .filter(item => !nextHiddenVerticals.includes(item))
                            .map(mapDefaultVerticalToProduct)
                            .find(item => item !== Product.None);

                        if (nextVisibleProduct) {
                            useProductTransitionStore.getState().actions.triggerTransition(nextVisibleProduct);
                        }
                    },
                }
            );
        },
        [availableVerticals, currentProduct, hiddenVerticals, navigateHomeOnBack, updateUserSettings]
    );

    return (
        <Screen>
            <ScreenNavBar
                title={t('rg:product_visibility')}
                onClose={
                    navigateHomeOnBack && enabledProduct
                        ? () => useProductTransitionStore.getState().actions.triggerTransition(enabledProduct)
                        : undefined
                }
            />
            <ScrollView>
                <MaxWidthWrapper paddingHorizontal={'s16'} paddingBottom={'s20'}>
                    <SizedBox value={8} />
                    <Text variant="headlineSmall">{t('rg:product_visibility_navigation_title')}</Text>
                    <SizedBox value={12} />
                    {availableVerticals.map((vertical, index) => {
                        const isVisible = !hiddenVerticals.includes(vertical);
                        const product = mapDefaultVerticalToProduct(vertical);
                        const isActiveProduct = product === currentProduct;
                        const isLastItem = index === availableVerticals.length - 1;
                        const isToggleDisabled = isActiveProduct && isVisible;

                        if (product === Product.None) {
                            return null;
                        }

                        return (
                            <React.Fragment key={vertical}>
                                <Row alignItems="center" justifyContent="space-between" paddingVertical="s16">
                                    <Text
                                        variant="titleMedium"
                                        color={isToggleDisabled ? 'neutral400' : ds.colorToken.text.primary}
                                    >
                                        {t(product)}
                                    </Text>
                                    <Switch
                                        val={isVisible}
                                        handleOnPress={() => handleToggle(vertical)}
                                        disabled={isToggleDisabled}
                                        editable={!isPending}
                                    />
                                </Row>
                                {!isLastItem ? <LineSeparator style={styles.separator} /> : null}
                            </React.Fragment>
                        );
                    })}
                </MaxWidthWrapper>
            </ScrollView>
        </Screen>
    );
};

const styles = StyleSheet.create({
    separator: {
        height: 0.33,
    },
});
